import type { WatchAnimationMode, WatchHotspotId, WatchInspectionMode, WatchVariant } from "@/demos/types";

export interface ExploreConfig {
  variant: WatchVariant;
  inspection: WatchInspectionMode;
  animation: WatchAnimationMode;
}

export const watchVariants: ReadonlyArray<{ id: WatchVariant; label: string; color: string }> = [
  { id: "surgical-white", label: "Surgical White", color: "#e7e7e4" },
  { id: "midnight-gold", label: "Midnight Gold", color: "#d8a44f" },
  { id: "commerce-green", label: "Commerce Green", color: "#2d6b42" },
  { id: "khronos-red", label: "Khronos Red", color: "#bd2727" },
];

export const watchHotspots: Readonly<Record<Exclude<WatchHotspotId, "none">, { label: string; description: string }>> = {
  band: { label: "Carbon-fibre band", description: "A lightweight band combining carbon-fibre detail with configurable polymer sections." },
  bezel: { label: "Protective bezel", description: "The raised bezel protects the glass while carrying the selected material finish." },
  buttons: { label: "Chronograph controls", description: "Dedicated metal and polymer controls demonstrate multi-material variant mapping." },
  glass: { label: "Transmissive glass", description: "A physically based transparent surface protects the dial and can be removed in technical view." },
  dial: { label: "Animated dial", description: "Separate hour, minute and second hands preserve product detail and authored animation." },
};

export const defaultExploreConfig: ExploreConfig = {
  variant: "midnight-gold",
  inspection: "assembled",
  animation: "running",
};
export type ExploreAction = { type: "set"; key: keyof ExploreConfig; value: string } | { type: "reset" };

const configVersion = "2";
const configKeys = ["variant", "inspection", "animation"] as const;

const allowedValues: { [Key in keyof ExploreConfig]: readonly ExploreConfig[Key][] } = {
  variant: watchVariants.map(({ id }) => id),
  inspection: ["assembled", "technical"],
  animation: ["running", "paused"],
};

export function exploreReducer(state: ExploreConfig, action: ExploreAction): ExploreConfig {
  if (action.type === "reset") return defaultExploreConfig;
  if (allowedValues[action.key].includes(action.value as never)) return { ...state, [action.key]: action.value } as ExploreConfig;
  return state;
}

export function parseExploreQuery(query: string): ExploreConfig {
  const params = new URLSearchParams(query.startsWith("?") ? query : `?${query}`);
  if (params.get("configVersion") !== configVersion) return defaultExploreConfig;
  if (configKeys.some((key) => {
    const value = params.get(key);
    return value !== null && !allowedValues[key].includes(value as never);
  })) return defaultExploreConfig;

  let state = defaultExploreConfig;
  for (const key of configKeys) {
    const value = params.get(key);
    if (value) state = exploreReducer(state, { type: "set", key, value });
  }
  return state;
}

export function serializeExploreQuery(config: ExploreConfig): string {
  return new URLSearchParams({ demo: "explore", configVersion, ...config }).toString();
}
